import Vue from 'vue'
import { FilterOption as IFilterOption } from 'services/api-service'
import { arrayFind } from 'util/ts-utils'

/**
 * Place to store mutable state pertaining to filters.
 * This is used to store the filters applied to the list of reviews.  It can be used to create
 * instances used for other filters in the future if needed.
 */

/**
 * A structure that allows quick access to an option for a particular filter
 * by group and filter name.
 */
export interface IFilters {
  [group: string]: {
    [filterName: string]: Array<IFilterOption>
  }
}

/**
 * Interface for the state property inside the store
 */
export interface IFiltersState {
  appliedFilters: IFilters
}

export interface IFlattenedFilter {
  group: string
  filterName: string
  option: IFilterOption
}


export class FilterStore {
  state: IFiltersState = {
    appliedFilters: {}
  }

  addFilterOption(group: string, filterName: string, option: IFilterOption) {
    if (!this.state.appliedFilters[group]) {
      // to make it reactive, need to let Vue know we're adding a property
      Vue.set(this.state.appliedFilters, group, {})
    }
    if (!this.state.appliedFilters[group][filterName]) {
      // to make it reactive, need to let Vue know we're adding a property
      Vue.set(this.state.appliedFilters[group], filterName, [])
    }
    // make sure it isn't already set
    if (!this.isFilterOptionSelected(group, filterName, option)) {
      this.state.appliedFilters[group][filterName].push(option)
    }
  }

  clearFilters() {
    this.state.appliedFilters = {}
  }

  getOptions(group: string, filterName: string) {
    return this.state.appliedFilters[group] ? this.state.appliedFilters[group][filterName] : null
  }

  isFilterOptionSelected(group: string, filterName: string, option: IFilterOption) {
    const options = this.getOptions(group, filterName)
    if (!options) {
      return false
    }
    return arrayFind(options, thisOption => thisOption.value === option.value) !== undefined
  }

  removeFilterOption(group: string, filterName: string, option: IFilterOption) {
    const options = this.getOptions(group, filterName)
    if (options) {
      for (let i = 0; i < options.length; i++) {
        if (options[i].value === option.value) {
          options.splice(i, 1)
          break
        }
      }
      // if no options are left, clear the group
      if (options.length === 0) {
        this.state.appliedFilters[group] = {}
      }
    }
  }

  // Unlike addFilterOption, this will override any existing options
  setFilter(group: string, filterName: string, options: Array<IFilterOption>) {
    if (!this.state.appliedFilters[group]) {
      // to make it reactive, need to let Vue know we're adding a property
      Vue.set(this.state.appliedFilters, group, {})
    }
    Vue.set(this.state.appliedFilters[group], filterName, options)
  }

  toggleFilterOption(group: string, filterName: string, option: IFilterOption) {
    const wasSelected = this.isFilterOptionSelected(group, filterName, option)
    if (wasSelected) {
      this.removeFilterOption(group, filterName, option)
    } else {
      this.addFilterOption(group, filterName, option)
    }
  }

  set filters(filters: IFilters) {
    this.state.appliedFilters = filters
  }

  get filters(): IFilters {
    return this.state.appliedFilters
  }

  /**
   * Flattens the filters so that there is one element in the array for every FilterOption.
   */
  get filtersFlattened(): Array<IFlattenedFilter> {
    const filters = this.state.appliedFilters
    const flattened = []
    Object.keys(filters).forEach((group) => {
      Object.keys(filters[group]).forEach((filterName) => {
        filters[group][filterName].forEach((option) => {
          flattened.push({
            group,
            filterName,
            option
          })
        })
      })
    })
    return flattened
  }

  /**
   * Takes the filters that have been set and formats them to be sent to the server in an encoded
   * JSON object.
   */
  get filtersEncodedForUrl(): string {
    const filters = this.state.appliedFilters
    const filtersForUrl = {}
    // Need to alphabetize the output (JSON.stringify) below so that the filter is concistent
    // for CDN caching. Idea for how:
    // https://stackoverflow.com/questions/16167581/sort-object-properties-and-json-stringify
    const replacer = []
    Object.keys(filters).sort().forEach((group) => {
      replacer.push(group)
      Object.keys(filters[group]).sort().forEach((filterName) => {
        replacer.push(filterName)
        if (!filtersForUrl[group]) {
          filtersForUrl[group] = {}
        }
        filtersForUrl[group][filterName] = filters[group][filterName].map(option => option.value)
      })
    })
    return encodeURIComponent(JSON.stringify(filtersForUrl, replacer.sort()))
  }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--1!(webpack)-strip-block!./src/store/filter-store.ts